import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

type GenerateMeetingNotePayload = {
  dataUploadFileId: string;
  templateId?: string;
  agendaFileId?: string;
  agendaInstruction?: string;
  meetingDate?: Date;
  summarise?: boolean;
};

type GenerateMeetingNoteResponse = {
  status: string;
  taskId: string;
  meetingNoteId: string;
  fileOwnerClientId: string;
};

type MutationInput = {
  resource: string;
  values: GenerateMeetingNotePayload;
};

export function useGenerateMeetingNote() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<{ data: GenerateMeetingNoteResponse }, Error, MutationInput>({
    mutationFn: ({ resource, values }: MutationInput) =>
      api.post<GenerateMeetingNoteResponse>(resource, values),
    onSettled: (response, _error, { resource }) => {
      queryClient.invalidateQueries({ queryKey: resource.split('/') });
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ['clients', response.data.fileOwnerClientId, 'meeting-notes'],
        });
      }
    },
  });

  const generateClientMeetingNote = async (
    clientId: string,
    values: GenerateMeetingNotePayload,
  ) => {
    return mutateAsync({ resource: `clients/${clientId}/meeting-notes`, values });
  };

  const generateFamilyMeetingNote = async (
    familyId: string,
    values: GenerateMeetingNotePayload,
  ) => {
    return mutateAsync({ resource: `families/${familyId}/meeting-notes`, values });
  };

  const generateMeetingNote = async (
    id: string,
    type: 'client' | 'family',
    values: GenerateMeetingNotePayload,
  ) => {
    if (type === 'client') {
      return generateClientMeetingNote(id, values);
    } else {
      return generateFamilyMeetingNote(id, values);
    }
  };

  return {
    generateMeetingNote,
    error,
    isLoading,
    reset,
  };
}
